import { useCallback } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Controller } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';

import { Loader } from '@components';
import { tinymceExtendedValidElements } from '@constants';
import { FormError, HelpText } from '@Forms/fields.jsx';
import { fileAsBase64 } from '@helpers';
import { imageMutation } from '@queries';

import './HTMLField.scss';

const RegisterPlugins = (props) => {
  MediaLibraryPlugin({ ...props });
};

const MediaLibraryPlugin = ({ editor, mutationImage }) => {
  const insertImage = (image) => {
    // insert the image and keep it selected; user can open dialog manually if desired
    editor.undoManager.transact(() => {
      const img = editor.dom.create('img', { src: image.absolute_path });
      editor.selection.setNode(img);
    });
    editor.focus();
  };

  // button in toolbar
  editor.ui.registry.addButton('medialibrary' /*name of the plugin*/, {
    //text: "Media Library",
    icon: 'gallery' /*You can pick your custom icon*/,
    tooltip: 'Upload new image',
    onAction: () => {
      triggerImageUpload(mutationImage, (image) => {
        insertImage(image);
      });
    },
  });

  // entry on context menu (right click menu in editor)
  editor.ui.registry.addMenuItem('medialibrary', {
    icon: 'gallery',
    text: 'Upload new image',
    onAction: () => {
      triggerImageUpload(mutationImage, (image) => {
        insertImage(image);
      });
    },
  });
  editor.ui.registry.addContextMenu('medialibrary', {
    update: (element) => {
      //!element.href ? '' : 'medialibrary'
      return 'medialibrary';
    },
  });
};

const triggerImageUpload = (mutationImage, callback) => {
  // force trigger a file element choice and upload and image with a callback
  const fileEl = document.createElement('input');
  fileEl.type = 'file';
  fileEl.accept = 'image/*';
  fileEl.addEventListener('change', (e) => {
    if (e.target.files.length) {
      console.log('file', e.target.files);
      fileAsBase64(e.target.files, (response) => {
        console.log('uploaded', response);
        // upload the image
        const payload = {
          type: 'image',
          upload: response,
        };
        mutationImage.mutate(payload, {
          onSuccess: (image) => {
            console.log('onSuccess', image);
            // trigger callback
            callback(image);
          },
        });
      });
    }
  });
  fileEl.click();
};

const HTMLField = ({
  control,
  error,
  editorRefs,
  height = 600,
  helpText,
  initialValue,
  label,
  name,
  register,
  ...props
}) => {
  // HTML field with full controls

  const mutationImage = useMutation(imageMutation());

  const setup = useCallback((editor) => {
    RegisterPlugins({ editor, mutationImage });
  }, []);

  return (
    <div
      className={
        `sb-form-item-formgroup sb-form-item-input ` +
        (error ? 'sb-form-item-error' : '')
      }
    >
      {label && <label htmlFor={name}>{label}</label>}
      <div className="sb-form-item-content">
        <div className="sb-tinymce-wrapper">
          {mutationImage.isPending && (
            <Loader className="sb-loader sb-loader-block" />
          )}
          <Controller
            control={control}
            name={name}
            render={({ field }) => (
              <Editor
                tinymceScriptSrc="/assets/tinymce/tinymce.min.js"
                licenseKey="gpl"
                onInit={(_evt, editor) => (editorRefs[name] = editor)}
                value={field.value ?? initialValue ?? ''}
                onEditorChange={field.onChange}
                init={{
                  branding: false,
                  menubar: false,
                  convert_urls: false, // disable tinymce manipulation of URLs (../../ etc)
                  plugins: [
                    'advlist',
                    'autolink',
                    'lists',
                    'link',
                    'image',
                    'charmap',
                    'preview',
                    'anchor',
                    'searchreplace',
                    'visualblocks',
                    'code',
                    'fullscreen',
                    'insertdatetime',
                    'media',
                    'table',
                    'help',
                    'wordcount',
                  ],
                  min_height: height,
                  height: height,
                  contextmenu: 'image link medialibrary',
                  toolbar:
                    'undo | blocks | bold italic superscript link | aligncenter | bullist numlist outdent indent | removeformat | medialibrary | table | code | help | fullscreen ',
                  content_style: `
                    body { font-family: Inter, system-ui, Avenir, Helvetica, Arial, sans-serif; line-height: 1.5; font-weight: 400; }
                    img { max-width: 100% !important; height: auto !important; }
                    .sb-mce-img-left { float: left; margin: 0 1rem 1rem 0; }
                    .sb-mce-img-right { float: right; margin: 0 0 1rem 1rem; }
                  `,
                  extended_valid_elements:
                    tinymceExtendedValidElements.join(','),
                  // Explicitly allow style tags to be children of the body
                  valid_children: '+body[style]',
                  image_advtab: true, // expose margin controls in image dialog
                  image_class_list: [
                    { title: 'Float (none)', value: '' },
                    { title: 'Float left', value: 'sb-mce-img-left' },
                    { title: 'Float right', value: 'sb-mce-img-right' },
                  ],
                  // disable iframe sandboxing to support iframes in HTML blocks
                  sandbox_iframes: false,
                  setup,
                }}
                {...props}
              />
            )}
          />
        </div>
        {helpText && <HelpText message={helpText} />}
        {error && <FormError message={error.message} />}
      </div>
    </div>
  );
};

export default HTMLField;
